/**
 * Wave 10 zod-mandate — runtime schemas for the outbox row + supporting
 * shapes (operations, status, progress snapshots).
 *
 * `payload` is `z.unknown()` by design — the outbox is a generic transport
 * for any consumer-shaped mutation; the server endpoint is the authority on
 * payload schema.
 *
 * @example
 * ```ts
 * import { EnqueueInputSchema } from './OutboxEntry.schema'
 * EnqueueInputSchema.parse({ table: 'rows', operation: 'create', payload: {} })
 * ```
 */
import { z } from 'zod'

export const OutboxOperationSchema = z.enum(['create', 'update', 'delete'])
export const OutboxStatusSchema = z.enum([
	'pending',
	'in_flight',
	'succeeded',
	'failed',
])

export const EnqueueInputSchema = z
	.object({
		table: z.string().min(1),
		operation: OutboxOperationSchema,
		payload: z.unknown(),
		baseVersion: z.number().int().nullable().optional(),
		clientEventUuid: z.string().min(1).optional(),
		// The host's optimistic placeholder key — opaque to the queue, so the only
		// constraint is "a non-empty string when present".
		tempId: z.string().min(1).optional(),
	})
	.strict()

export const ProgressSnapshotSchema = z
	.object({
		pending: z.number().int().nonnegative(),
		in_flight: z.number().int().nonnegative(),
		succeeded: z.number().int().nonnegative(),
		failed: z.number().int().nonnegative(),
	})
	.strict()

export const OutboxEntrySchema = z
	.object({
		id: z.number().int(),
		table: z.string().min(1),
		operation: OutboxOperationSchema,
		payload: z.unknown(),
		attempts: z.number().int().nonnegative(),
		last_attempt_at: z.number().nullable(),
		status: OutboxStatusSchema,
		client_event_uuid: z.string().min(1),
		created_at: z.number(),
		base_version: z.number().int().nullable(),
		last_error: z.string().nullable(),
		// Optional, NOT required: rows persisted by a pre-0.4 build carry no
		// `temp_id` column, and they must keep parsing after an upgrade.
		temp_id: z.string().nullable().optional(),
	})
	.strict()

export type EnqueueInputInput = z.infer<typeof EnqueueInputSchema>
export type OutboxOperationInput = z.infer<typeof OutboxOperationSchema>
export type OutboxStatusInput = z.infer<typeof OutboxStatusSchema>
export type ProgressSnapshotInput = z.infer<typeof ProgressSnapshotSchema>
export type OutboxEntryInput = z.infer<typeof OutboxEntrySchema>
